import * as React from "react";

import * as fieldsTheme from "@impero-com/common/components/form/fields/theme.css";
import { getParentElement } from "@impero-com/common/utils/dom";
import { TimePicker as AntdTimePicker } from "antd";
import { cx } from "class-variance-authority";
import moment, { Moment } from "moment-timezone";

interface Time {
    hour: number;
    minute: number;
}

type AntdTimePickerProps = React.ComponentProps<typeof AntdTimePicker>;

type TimePickerProps = Omit<
    React.ComponentProps<typeof AntdTimePicker>,
    "defaultValue" | "value" | "onChange"
> & {
    defaultValue?: Time;
    value?: Time;
    onChange?: (time: Time | undefined) => void;
};

// Wrapper around antd's TimePicker that uses more specific Time type instead of Moment.
class TimePicker extends React.PureComponent<TimePickerProps> {
    render = () => {
        const antdProps = convertProps(this.props);
        return <AntdTimePicker {...antdProps} />;
    };
}

function convertProps(props: TimePickerProps): AntdTimePickerProps {
    let defaultValue: Moment | undefined;
    if (props.defaultValue !== undefined) {
        defaultValue = timeToMoment(props.defaultValue);
    }

    let value: Moment | undefined;
    if (props.value !== undefined) {
        value = timeToMoment(props.value);
    }

    let onChange: ((m: Moment | null) => void) | undefined;
    if (props.onChange) {
        const timeOnChange = props.onChange;
        onChange = (m: Moment | null) => {
            if (m !== null) {
                const time = momentToTime(m);
                timeOnChange(time);
            } else {
                timeOnChange(undefined);
            }
        };
    }

    return {
        ...props,
        popupClassName: cx(fieldsTheme.timePickerPopup, props.popupClassName),
        getPopupContainer: props.getPopupContainer ?? getParentElement,
        defaultValue,
        value,
        onChange,
    };
}

function timeToMoment(t: Time): Moment {
    return moment({ hour: t.hour, minute: t.minute });
}

function momentToTime(m: Moment): Time {
    return {
        hour: m.hour(),
        minute: m.minute(),
    };
}

function timeFromString(timeStr: string): Time {
    const time = moment(timeStr, ["HH:mm"]);
    return momentToTime(time);
}

function timeToString(time: Time): string {
    return `${time.hour}:${time.minute}`;
}

export {
    TimePicker,
    type TimePickerProps,
    type Time,
    timeFromString,
    timeToString,
};
